import { FC } from 'react';
import Modal from '@/components/modals/Modal/Modal.tsx';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import { z } from 'zod';
import SocialMediaName, { allSocialMediaNames } from '@/_constants/SocialMediaName.ts';
import CheckboxInput from '@/components/functional/CheckboxInput/CheckboxInput.tsx';
import NameLengthType, { allNameLengthTypes } from '@/_constants/NameLengthType.ts';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import useUserContextSave from '@/api/chat/useUserContextSave.ts';
import { Alert } from 'react-daisyui';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useSearchContext } from '@/context/SearchContext.tsx';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import { UserContext } from '@/models/UserContext';

type UserContextModalProps = {
  isOpen: boolean;
  chatId: string;
  onClose?: () => void;
  context?: Partial<Omit<UserContext, 'id'>> | null;
};

const schema = z.object({
  business_desc: z.string().max(150, { message: 'Max 150 characters' }).optional(),
  keywords: z.string().max(150, { message: 'Max 150 characters' }).optional(),
  platform_focus: z
    .array(z.enum(allSocialMediaNames, { message: 'Invalid Social Media' }))
    .min(1, { message: 'At least one Social Media is required' }),
  style_prefs: z
    .object({
      length: z.enum(allNameLengthTypes).optional(),
      style: z.string().max(30, { message: 'Max 30 characters' }).optional(),
    })
    .optional(),
  tld_pref: z.string().max(10, { message: 'Max 10 characters' }).optional(),
  locale: z.string().max(10, { message: 'Max 10 characters' }).optional(),
});

type Schema = z.infer<typeof schema>;

const UserContextModal: FC<UserContextModalProps> = ({ isOpen, onClose, chatId, context }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { handleSetLocalUserContext } = useSearchContext();
  const { control, handleSubmit } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      business_desc: context?.business_desc ?? undefined,
      keywords: context?.keywords ? context.keywords.join(', ') : undefined,
      platform_focus: context?.platform_focus ?? [SocialMediaName.FACEBOOK, SocialMediaName.INSTAGRAM],
      style_prefs: {
        length: context?.style_prefs?.length ?? NameLengthType.SHORT,
        style: context?.style_prefs?.style ?? undefined,
      },
      tld_pref: context?.tld_pref ?? undefined,
      locale: context?.locale ?? undefined,
    },
  });

  const {
    mutateAsync: saveUserContext,
    isPending: isLoadingSaveUserContext,
    error: errorSaveUserContext,
  } = useUserContextSave();

  const handleContinue = (data: Schema, closeCallbackFn: () => void) => {
    (async () => {
      try {
        if (isLoadingSaveUserContext) return;

        const keywordsArray = data.keywords
          ?.split(',')
          .map((kw) => kw.trim())
          .filter((kw) => kw.length > 0);

        const context = {
          chat_id: chatId,
          ...data,
          keywords: keywordsArray,
        };

        if (user?.consents?.storage) {
          await saveUserContext(context);
        }

        handleSetLocalUserContext(context);

        closeCallbackFn();
      } catch (e) {
        console.error('Error in saving UserContext: ', e);
      }
    })();
  };

  const handleSkip = (closeCallbackFn: () => void) => {
    closeCallbackFn();
  };

  return (
    <Modal.Wrapper isOpen={isOpen} onClose={onClose} closeable={false} className="flex flex-col pb-2 gap-4 w-[800px]">
      <Modal.Title>
        <h1 className="text-2xl font-bold">{t('modal.userContext.title')}</h1>
      </Modal.Title>
      <p className="text-text-secondary text-sm">{t('modal.userContext.description')}</p>
      {!user?.consents?.storage && (
        <p className="text-text-secondary text-sm">
          <Trans i18nKey="other.keep_saved_user_context">
            To keep chat context between sessions, update your storage consent on
            <Link className="font-bold text-primary-400" to={Routes.SETTINGS__CONSENTS}>
              settings page
            </Link>
            .
          </Trans>
        </p>
      )}
      {errorSaveUserContext ? <Alert status="error">{errorSaveUserContext.message}</Alert> : null}
      <Controller
        control={control}
        name="locale"
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <SelectInput
            wrapperClassName="z-0"
            label={t('label.locale')}
            error={errors?.locale?.message}
            value={value ?? ''}
            onChange={onChange}
            placeholder={t('placeholder.locale')}
            searchable
            options={[
              { value: 'en_US', label: 'English (US)' },
              { value: 'en_GB', label: 'English (UK)' },
              { value: 'es_ES', label: 'Spanish (Spain)' },
              { value: 'es_MX', label: 'Spanish (Mexico)' },
              { value: 'fr_FR', label: 'French (France)' },
              { value: 'fr_CA', label: 'French (Canada)' },
              { value: 'de_DE', label: 'German (Germany)' },
              { value: 'it_IT', label: 'Italian (Italy)' },
              { value: 'pt_BR', label: 'Portuguese (Brazil)' },
              { value: 'pt_PT', label: 'Portuguese (Portugal)' },
              { value: 'pl_PL', label: 'Polish (Poland)' },
              { value: 'ja_JP', label: 'Japanese (Japan)' },
              { value: 'zh_CN', label: 'Chinese (Simplified)' },
              { value: 'zh_TW', label: 'Chinese (Traditional)' },
              { value: 'ko_KR', label: 'Korean (South Korea)' },
              { value: 'ru_RU', label: 'Russian (Russia)' },
              { value: 'ar_SA', label: 'Arabic (Saudi Arabia)' },
              { value: 'hi_IN', label: 'Hindi (India)' },
              { value: 'bn_BD', label: 'Bengali (Bangladesh)' },
              { value: 'ur_PK', label: 'Urdu (Pakistan)' },
              { value: 'tr_TR', label: 'Turkish (Turkey)' },
              { value: 'vi_VN', label: 'Vietnamese (Vietnam)' },
              { value: 'th_TH', label: 'Thai (Thailand)' },
              { value: 'ms_MY', label: 'Malay (Malaysia)' },
              { value: 'id_ID', label: 'Indonesian (Indonesia)' },
              { value: 'fa_IR', label: 'Persian (Iran)' },
              { value: 'he_IL', label: 'Hebrew (Israel)' },
              { value: 'nl_NL', label: 'Dutch (Netherlands)' },
              { value: 'sv_SE', label: 'Swedish (Sweden)' },
              { value: 'no_NO', label: 'Norwegian (Norway)' },
              { value: 'fi_FI', label: 'Finnish (Finland)' },
              { value: 'da_DK', label: 'Danish (Denmark)' },
            ]}
          />
        )}
      />
      <Controller
        control={control}
        name="business_desc"
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <TextInput
            label={t('label.business_desc')}
            error={errors?.business_desc?.message}
            value={value ?? ''}
            onChange={onChange}
            placeholder={t('placeholder.business_desc')}
          />
        )}
      />
      <Controller
        control={control}
        name="keywords"
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <TextInput
            label={t('label.keywords')}
            error={errors?.keywords?.message}
            value={value ?? ''}
            onChange={onChange}
            placeholder={t('placeholder.keywords')}
          />
        )}
      />
      <Controller
        control={control}
        name="style_prefs.style"
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <TextInput
            label={t('label.style_prefs.style')}
            error={errors?.style_prefs?.style?.message}
            value={value ?? ''}
            onChange={onChange}
            placeholder={t('placeholder.style_prefs.style')}
          />
        )}
      />
      <Controller
        control={control}
        name="style_prefs.length"
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <div className="flex flex-col gap-2">
            <p className="text-sm">{t('label.style_prefs.length')}</p>
            <div className="flex flex-wrap gap-x-2">
              {allNameLengthTypes.map((type) => (
                <CheckboxInput
                  key={type}
                  label={t(`label.nameLengthType.${type}`, type)}
                  value={type}
                  checked={value === type}
                  color="primary"
                  onChange={() => onChange(type)}
                  error={errors.style_prefs?.length?.message}
                />
              ))}
            </div>
          </div>
        )}
      />
      <Controller
        control={control}
        name="tld_pref"
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <TextInput
            label={t('label.tld_pref')}
            error={errors?.tld_pref?.message}
            value={value ?? ''}
            onChange={onChange}
            placeholder={t('placeholder.tld_pref')}
          />
        )}
      />
      <Controller
        control={control}
        name="platform_focus"
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <div className="flex flex-col gap-2">
            <p className="text-sm">{t('label.platform_focus')}</p>
            <div className="flex flex-wrap gap-x-2">
              {allSocialMediaNames.map((socialMedia) => (
                <CheckboxInput
                  key={socialMedia}
                  label={<span className="cursor-pointer">{socialMedia}</span>}
                  value={socialMedia}
                  checked={value?.includes(socialMedia) ?? false}
                  color="primary"
                  onChange={() =>
                    onChange(
                      value?.includes(socialMedia)
                        ? value?.filter((d) => d !== socialMedia)
                        : [...(value ?? []), socialMedia]
                    )
                  }
                  error={errors.platform_focus?.message}
                />
              ))}
            </div>
          </div>
        )}
      />
      <Modal.Nav>
        {({ handleClose }) => (
          <form
            className="flex justify-between gap-4 grow"
            onSubmit={handleSubmit((data) => handleContinue(data, handleClose))}
          >
            <LoadingButton
              loading={isLoadingSaveUserContext}
              type="button"
              variant="outline"
              color="error"
              onClick={() => handleSkip(handleClose)}
            >
              {t('button.skip')}
            </LoadingButton>
            <LoadingButton loading={isLoadingSaveUserContext} type="submit" color="primary">
              {t('button.continue')}
            </LoadingButton>
          </form>
        )}
      </Modal.Nav>
    </Modal.Wrapper>
  );
};

export default UserContextModal;
